import { IProjectTemplate } from '@d-id/types';
import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import { MasonryPhotoAlbum, Photo } from 'react-photo-album';
import { BREAKPOINTS, COLUMNS } from '../constants';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { START_FROM_BLANK_TEMPLATE, StartFromBlank } from './StartFromBlank';
import { generateSkeletonPhotos, SkeletonTemplate } from './TemplateGallerySkeleton';
import { TemplateTile } from './TemplateTile';

export const StyledScrollArea = styled.div`
    overflow-y: auto;
    height: 100%;
    padding: 0 24px;
    scrollbar-gutter: stable;
`;

interface TemplateGalleryContentProps {
    isLoading: boolean;
    isError: boolean;
    photos: (IProjectTemplate & Photo & { imageHeight: number })[];
    setSelectedTemplate: (template: IProjectTemplate | null, index: number | null) => void;
    onStartFromBlank: () => Promise<void>;
    searchQuery: string;
    onRetry: () => void;
}

export const TemplateGalleryContent = ({
    isLoading,
    isError,
    photos,
    setSelectedTemplate,
    onStartFromBlank,
    searchQuery,
    onRetry,
}: TemplateGalleryContentProps) => {
    const [firstItemWidth, setFirstItemWidth] = useState<number>(0);

    const templates = useMemo(() => {
        if (isLoading) {
            return generateSkeletonPhotos() as Photo[];
        }
        return photos;
    }, [isLoading, photos]);

    return (
        <StyledScrollArea>
            {!isLoading && <StartFromBlank onClick={onStartFromBlank} width={firstItemWidth || START_FROM_BLANK_TEMPLATE.width} />}
            <MasonryPhotoAlbum
                componentsProps={{ container: { style: { paddingBottom: '24px' } } }}
                photos={templates}
                key={`${templates.length}-mansonry-${isLoading ? 'loading' : 'loaded'}`}
                spacing={16}
                columns={containerWidth => {
                    if (containerWidth > BREAKPOINTS.LARGE) return COLUMNS.LARGE;
                    if (containerWidth > BREAKPOINTS.MEDIUM) return COLUMNS.MEDIUM;
                    if (containerWidth > BREAKPOINTS.SMALL) return COLUMNS.SMALL;
                    return COLUMNS.XSMALL;
                }}
                render={{
                    photo: (_props, { photo, index }) => {
                        if ('isSkeleton' in photo) {
                            const skeletonPhoto = photo as unknown as SkeletonTemplate;
                            return <SkeletonTemplate key={`skel-${index}`} aspectRatio={skeletonPhoto.aspectRatio} />;
                        }

                        const template = photo as unknown as TemplateGalleryContentProps['photos'][number];
                        return (
                            <TemplateTile
                                index={index}
                                key={template.id}
                                template={template}
                                setSelectedTemplate={setSelectedTemplate}
                                onLayout={index === 0 ? setFirstItemWidth : undefined}
                            />
                        );
                    },
                }}
            />
            {!isLoading && !isError && templates.length === 0 && <EmptyState searchQuery={searchQuery} />}
            {isError && <ErrorState onRetry={onRetry} />}
        </StyledScrollArea>
    );
};
